import * as Api from '@ViewModels';
import { InsurancePolicyFilterProperty } from '@ViewModels';
import moment from 'moment';
import { IPolicyFilterPeriod } from '../components/PoliciesFilters';

export const HISTORY_CHARTS: Api.IPolicyHistoryChartQuery[] = [
	{ key: 'inForce', measure: 'InForce' },
	{ key: 'policyHolders', measure: 'PolicyHolders' },
	{ key: 'newBusiness', measure: 'NewBusiness' },
	{ key: 'renewed', measure: 'Renewed' },
	{ key: 'lost', measure: 'Lost' },
	{ key: 'assumedCancelled', measure: 'AssumedCancelled' },
	{ key: 'pendingLoss', measure: 'PendingLoss' },
	{ key: 'newBusinessByProducer', measure: 'NewBusiness', seriesBy: 'Owner' },
	{ key: 'renewalsDue', measure: 'RenewalsDue' },
	{ key: 'renewalsRetained', measure: 'RenewalsRetained' },
];

const collect = (criteria: Api.IInsurancePolicyFilterCriteria[], property: InsurancePolicyFilterProperty): string[] =>
	criteria.flatMap(c =>
		c.criteria?.length ? collect(c.criteria, property) : c.property === property && c.value ? [c.value] : []
	);

/** The history API filters only by carrier, owner and line of business, over a period. */
export const toHistoryFilter = (
	criteria: Api.IInsurancePolicyFilterCriteria[],
	period: IPolicyFilterPeriod
): Api.IPolicyHistoryChartFilter => {
	const filter: Api.IPolicyHistoryChartFilter = {};
	if (period.from) filter.from = period.from;
	if (period.to) filter.to = period.to;
	const carrierIds = collect(criteria, InsurancePolicyFilterProperty.Carrier);
	const ownerIds = collect(criteria, InsurancePolicyFilterProperty.OwnerId);
	const lineOfBusinessIds = collect(criteria, InsurancePolicyFilterProperty.LineOfBusiness);
	if (carrierIds.length) filter.carrierIds = carrierIds;
	if (ownerIds.length) filter.ownerIds = ownerIds;
	if (lineOfBusinessIds.length) filter.lineOfBusinessIds = lineOfBusinessIds;
	return filter;
};

export interface IHistoryAmount {
	count: number;
	value: number;
}

export interface IInForcePoint extends IHistoryAmount {
	date: string;
}

export const readInForce = (
	chart: Pick<Api.IPolicyHistoryChart, 'data'> | undefined
): { start: IInForcePoint | null; end: IInForcePoint | null } => {
	const points = [...(chart?.data ?? [])].sort((a, b) => a.label.localeCompare(b.label));
	const toPoint = (p?: Api.IPolicyHistoryChartDataPoint): IInForcePoint | null =>
		p ? { date: p.label, count: p.count, value: p.value } : null;
	return { start: toPoint(points[0]), end: toPoint(points[points.length - 1]) };
};

export type HistoryStage = 'noSnapshot' | 'justStarted' | 'tracking';

/** Change and monthly movement only mean something once a whole calendar month has been tracked. */
export const firstFullMonth = (baselineCompleted: string) => moment(baselineCompleted).add(1, 'month').startOf('month');

export const getHistoryStage = ({
	baselineCompleted,
	today = moment(),
}: {
	baselineCompleted?: string;
	today?: moment.Moment;
}): HistoryStage => {
	if (!baselineCompleted) {
		return 'noSnapshot';
	}
	return today.isBefore(firstFullMonth(baselineCompleted)) ? 'justStarted' : 'tracking';
};

export interface IMovementRow {
	month: string;
	newBusiness: IHistoryAmount;
	renewed: IHistoryAmount;
	lost: IHistoryAmount;
	assumedCancelled: IHistoryAmount;
	pendingLoss: IHistoryAmount;
	/** Some losses this month are still inside the confirmation window. */
	preliminary: boolean;
}

const MOVEMENT_KEYS = ['newBusiness', 'renewed', 'lost', 'assumedCancelled', 'pendingLoss'] as const;

export const readMovement = (response: Api.IPolicyHistoryChartsResponse | undefined): IMovementRow[] => {
	if (!response) {
		return [];
	}
	const months = [
		...new Set(MOVEMENT_KEYS.flatMap(key => response.charts?.[key]?.data?.map(p => p.label) ?? [])),
	].sort();
	const amount = (key: (typeof MOVEMENT_KEYS)[number], month: string): IHistoryAmount => {
		const p = response.charts?.[key]?.data?.find(d => d.label === month);
		return { count: p?.count ?? 0, value: p?.value ?? 0 };
	};
	return months.map(month => ({
		month,
		newBusiness: amount('newBusiness', month),
		renewed: amount('renewed', month),
		lost: amount('lost', month),
		assumedCancelled: amount('assumedCancelled', month),
		pendingLoss: amount('pendingLoss', month),
		preliminary: !!response.confirmedThrough && moment(month).endOf('month').isAfter(response.confirmedThrough),
	}));
};

export interface IProducerRow {
	id: string;
	label: string;
	cells: number[];
	total: number;
}

const OTHER_SERIES_ID = 'other';

/** New business premium per producer for the last `monthCount` months, highest total first and "Other" last. */
export const readProducerMatrix = (
	chart: Pick<Api.IPolicyHistoryChart, 'data'> | undefined,
	monthCount: number
): { months: string[]; rows: IProducerRow[] } => {
	const data = chart?.data ?? [];
	const months = [...new Set(data.map(p => p.label))].sort().slice(-monthCount);
	const bySeries = new Map<string, IProducerRow>();
	for (const p of data) {
		const monthIndex = months.indexOf(p.label);
		if (monthIndex < 0 || !p.seriesId) {
			continue;
		}
		const row = bySeries.get(p.seriesId) ?? {
			id: p.seriesId,
			label: p.seriesLabel ?? p.seriesId,
			cells: months.map(() => 0),
			total: 0,
		};
		row.cells[monthIndex] += p.value;
		row.total += p.value;
		bySeries.set(p.seriesId, row);
	}
	const rows = [...bySeries.values()].sort((a, b) => {
		if (a.id === OTHER_SERIES_ID) return 1;
		if (b.id === OTHER_SERIES_ID) return -1;
		return b.total - a.total;
	});
	return { months, rows };
};

export interface IRetention {
	due: number;
	kept: number;
}

const totalCount = (chart?: Pick<Api.IPolicyHistoryChart, 'data'>) =>
	(chart?.data ?? []).reduce((sum, point) => sum + point.count, 0);

/** Retention over the whole period: renewals kept out of renewals due, not an average of monthly rates. */
export const readRetention = (response: Api.IPolicyHistoryChartsResponse | undefined): IRetention => ({
	due: totalCount(response?.charts?.renewalsDue),
	kept: totalCount(response?.charts?.renewalsRetained),
});
